import {IThemeService, PreferredColorScheme} from "./theme.service.interface";
import {makeObservable, observable, runInAction} from "mobx";
import {ServiceBase} from "../service-base";
import {IServiceFactory} from "../service-factory.interface";
import {IAssetResolver} from "./asset-resolver.interface";
import {makeTheme} from "./helpers/make-theme";
import {Check} from "../../types/type-checking";
import {ContainerMediaQueries} from "../media-query/container/container-media-queries";
import {AirlineTheme} from "./interfaces/airline.theme";
import {EnvLocalStorageKeys} from "../storage/environment-local-storage/environment-local-storage-keys";

//https://ionicframework.com/docs/theming/dark-mode
export class ThemeService extends ServiceBase implements IThemeService, IAssetResolver {

    constructor(services: IServiceFactory) {
        super(services);
        this._userPreferredColorScheme = (this.services.envLocalStorage.getItem(EnvLocalStorageKeys.ThemePreferredColorScheme) as PreferredColorScheme.System) ?? PreferredColorScheme.System;
        const colorSchemeDarkMatchMedia = window.matchMedia('(prefers-color-scheme: dark)');
        this._setCurrentSystemColorScheme(colorSchemeDarkMatchMedia.matches);
        this.currentTheme = makeTheme(this);

        if(colorSchemeDarkMatchMedia.addEventListener){
            colorSchemeDarkMatchMedia.addEventListener('change', this._systemColorSchemeChangeHandler);
        }
        else{
            colorSchemeDarkMatchMedia.addListener(this._systemColorSchemeChangeHandler); //for old ios (<= 12) versions
        }

        makeObservable(this, {
            _systemColorScheme: observable.ref,
            _userPreferredColorScheme: observable.ref
        })
    }

    _systemColorScheme: PreferredColorScheme = PreferredColorScheme.Light;
    _userPreferredColorScheme: PreferredColorScheme = PreferredColorScheme.System;

    currentTheme: AirlineTheme;

    applyContainerMediaQueries(): AirlineTheme {
        return {
            ...this.currentTheme,
            containerMediaQuery: ContainerMediaQueries
        }
    }

    get isInDarkMode(): boolean {
        return this.appColorScheme === PreferredColorScheme.Dark;
    }

    setColorScheme(colorScheme:PreferredColorScheme): void {
        if(colorScheme === this.appColorScheme) {
            return;
        }
        runInAction(() => {
            this._userPreferredColorScheme = colorScheme;
            this.services.envLocalStorage.setItem(EnvLocalStorageKeys.ThemePreferredColorScheme, this._userPreferredColorScheme);
            this._updateAppColorScheme();
        });
    }

    private get appColorScheme(): PreferredColorScheme {
        if(Check.isNullOrUndefined(this._userPreferredColorScheme)
            || this._userPreferredColorScheme === PreferredColorScheme.System) {
            return this._systemColorScheme;
        }

        return this._userPreferredColorScheme;
    }

    private _systemColorSchemeChangeHandler = (event: MediaQueryListEvent) => {
        runInAction(() => {
            this._setCurrentSystemColorScheme(event.matches);
        });
    }

    private _setCurrentSystemColorScheme(useDarkTheme: boolean): void {
        runInAction(() => {
            if(useDarkTheme) {
                this._systemColorScheme = PreferredColorScheme.Dark;
            } else {
                this._systemColorScheme = PreferredColorScheme.Light;
            }

            this._updateAppColorScheme();
        });

    }


    private _updateAppColorScheme(): void {
        this.services.document.body.setAttribute('color-scheme', this.appColorScheme);
    }

    resolveAssetPath(relativePath: string, supportedLocalizations: string[]): string {
        const assetUrl = new URL(this.services.window.getOrigin());
        const currentLanguage = this.services.language.currentLanguage;
        if(0 <= supportedLocalizations.findIndex(l => l === currentLanguage)) {
            assetUrl.pathname = `/assets/localized/${currentLanguage}/${relativePath}`;
        } else {
            assetUrl.pathname = `/assets/defaults/${relativePath}`;
        }

        return assetUrl.href;
    }

    private _convertToPixels(obj: any): any {
        const result: Record<string, string> = {};

        for(let key of Object.keys(obj)) {
            const value = 16 * parseFloat(obj[key].replace('rem', ''));
            if(Check.isNumber(value)) {
                result[key] = value + 'px';
            } else {
                result[key] = obj[key];
            }

        }
        return result;
    }
    createSearchMaskTheme(): AirlineTheme {
        return {
            ...this.currentTheme,
            fontSize: this._convertToPixels(this.currentTheme.fontSize),
            spacing: this._convertToPixels(this.currentTheme.spacing)
        };
    }
}
